import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { authenticateWithBiometrics } from '@/lib/webauthn';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auto logout (inactivity) settings - stored per device
const ACTIVITY_KEY = 'livon_last_activity';
const AUTO_LOGOUT_KEY = 'livon_auto_logout_minutes';
const DEFAULT_AUTO_LOGOUT_MINUTES = 30;

const getStoredAutoLogoutMinutes = () => {
  const v = parseInt(localStorage.getItem(AUTO_LOGOUT_KEY), 10);
  return Number.isNaN(v) ? DEFAULT_AUTO_LOGOUT_MINUTES : v;
};

const isInactivityExpired = (minutes) => {
  if (!minutes || minutes <= 0) return false;
  const last = parseInt(localStorage.getItem(ACTIVITY_KEY), 10);
  if (Number.isNaN(last)) return false;
  return Date.now() - last > minutes * 60 * 1000;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));
  const [loading, setLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [pending2FAUserId, setPending2FAUserId] = useState(null);
  const [autoLogoutMinutes, setAutoLogoutMinutesState] = useState(getStoredAutoLogoutMinutes());

  const clearAuthState = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(ACTIVITY_KEY);
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      // Inactivity check on app start (e.g. PWA reopened after being closed)
      if (token && isInactivityExpired(getStoredAutoLogoutMinutes())) {
        clearAuthState();
        setLoading(false);
        toast.info('Du loggades ut automatiskt på grund av inaktivitet.');
        return;
      }
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          // Try to refresh token
          if (refreshToken && error.response?.status === 401) {
            try {
              const refreshResponse = await axios.post(`${API}/auth/refresh`, {
                refresh_token: refreshToken
              });
              const newToken = refreshResponse.data.access_token;
              localStorage.setItem('token', newToken);
              setToken(newToken);
              
              const userResponse = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${newToken}` }
              });
              setUser(userResponse.data);
            } catch (refreshError) {
              // Refresh failed, logout
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              setToken(null);
              setRefreshToken(null);
              setUser(null);
            }
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            setToken(null);
            setRefreshToken(null);
            setUser(null);
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token, refreshToken, clearAuthState]);

  // Track user activity and auto-logout after inactivity
  useEffect(() => {
    if (!user) return;

    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));

    let lastWrite = Date.now();
    const recordActivity = () => {
      const now = Date.now();
      if (now - lastWrite > 15000) {
        lastWrite = now;
        localStorage.setItem(ACTIVITY_KEY, String(now));
      }
    };

    const performAutoLogout = () => {
      clearAuthState();
      toast.info('Du loggades ut automatiskt på grund av inaktivitet.');
    };

    const checkExpiry = () => {
      if (isInactivityExpired(getStoredAutoLogoutMinutes())) {
        performAutoLogout();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkExpiry();
      }
    };

    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, recordActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibilityChange);
    const interval = setInterval(checkExpiry, 30000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, recordActivity));
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(interval);
    };
  }, [user, autoLogoutMinutes, clearAuthState]);

  const setAutoLogoutMinutes = (minutes) => {
    localStorage.setItem(AUTO_LOGOUT_KEY, String(minutes));
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    setAutoLogoutMinutesState(minutes);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    
    // Check if 2FA is required
    if (response.data.requires_2fa) {
      setRequires2FA(true);
      setPending2FAUserId(response.data.user_id);
      return { requires_2fa: true };
    }
    
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
      setRefreshToken(refresh_token);
    }
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const loginWithBiometrics = async (email) => {
    const data = await authenticateWithBiometrics(email);
    const { access_token, refresh_token, user: userData } = data;
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
      setRefreshToken(refresh_token);
    }
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const verify2FA = async (code) => {
    const response = await axios.post(`${API}/auth/verify-2fa`, {
      user_id: pending2FAUserId,
      code: code
    });
    
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
      setRefreshToken(refresh_token);
    }
    setToken(access_token);
    setUser(userData);
    setRequires2FA(false);
    setPending2FAUserId(null);
    return userData;
  };

  const cancel2FA = () => {
    setRequires2FA(false);
    setPending2FAUserId(null);
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
      setRefreshToken(refresh_token);
    }
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    // Call backend logout to invalidate session
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        // Ignore errors during logout
      }
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(ACTIVITY_KEY);
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error('Could not refresh user');
      }
    }
  };

  // User has access if premium OR within trial period
  // Preview mode: always grant access for testing
  const isPreview = window.location.hostname.includes('preview.emergentagent.com');
  const isPremium = user?.is_premium || false;
  const isTrial = user?.is_trial || false;
  const trialDaysLeft = user?.trial_days_left || 0;
  const hasAccess = isPreview || isPremium || isTrial;
  const has2FAEnabled = user?.two_fa_enabled || false;

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      getAuthHeader, 
      refreshUser, 
      isPremium,
      isTrial,
      trialDaysLeft,
      hasAccess,
      has2FAEnabled,
      requires2FA,
      verify2FA,
      cancel2FA,
      loginWithBiometrics,
      autoLogoutMinutes,
      setAutoLogoutMinutes
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
